import React, { useEffect, useState, useRef, lazy } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ShoppingBagIcon, CheckIcon, XIcon } from 'lucide-react';
import { scanPageProducts, pickFeaturedProduct, ScannedProduct } from './productScanner';
const BUYERS = [{
  name: 'María',
  place: 'Ciudad de México'
}, {
  name: 'Carlos',
  place: 'Bogotá'
}, {
  name: 'Lucía',
  place: 'Buenos Aires'
}, {
  name: 'Andrés',
  place: 'Madrid'
}, {
  name: 'Valentina',
  place: 'Lima'
}, {
  name: 'Diego',
  place: 'Santiago'
}, {
  name: 'Sofía',
  place: 'Guadalajara'
}, {
  name: 'Mateo',
  place: 'Medellín'
}, {
  name: 'Camila',
  place: 'Quito'
}, {
  name: 'Javier',
  place: 'Monterrey'
}, {
  name: 'Daniela',
  place: 'San José'
}, {
  name: 'Tomás',
  place: 'Montevideo'
}];
const TIME_AGO = ['hace 2 minutos', 'hace 5 minutos', 'hace 8 minutos', 'hace 11 minutos', 'hace unos segundos', 'hace 1 minuto'];
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
interface ToastData {
  id: number;
  buyer: string;
  place: string;
  timeAgo: string;
  product: ScannedProduct;
}
export function RecentPurchaseToast() {
  const [catalog, setCatalog] = useState<ScannedProduct[]>([]);
  const [toast, setToast] = useState<ToastData | null>(null);
  const idRef = useRef(0);
  const dismissTimer = useRef<ReturnType<typeof setTimeout>>();
  // Scan the page after it has rendered so DOM products are available.
  useEffect(() => {
    const t = setTimeout(() => setCatalog(scanPageProducts()), 1200);
    return () => clearTimeout(t);
  }, []);
  // Periodically show a new purchase notification.
  useEffect(() => {
    let cycleTimer: ReturnType<typeof setTimeout>;
    const showOne = () => {
      const buyer = pick(BUYERS);
      idRef.current += 1;
      setToast({
        id: idRef.current,
        buyer: buyer.name,
        place: buyer.place,
        timeAgo: pick(TIME_AGO),
        product: pickFeaturedProduct(catalog)
      });
      // Auto-hide after a few seconds, then schedule the next one.
      dismissTimer.current = setTimeout(() => setToast(null), 6000);
      cycleTimer = setTimeout(showOne, 6000 + 7000 + Math.random() * 6000);
    };
    // First toast shortly after the scan completes.
    const startTimer = setTimeout(showOne, 4000);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(cycleTimer);
      clearTimeout(dismissTimer.current);
    };
  }, [catalog]);
  const close = () => {
    clearTimeout(dismissTimer.current);
    setToast(null);
  };
  return <div className="pointer-events-none fixed bottom-24 left-4 z-[55] sm:bottom-24 sm:left-5" data-id="element-233">
      <AnimatePresence data-id="element-234">
        {toast && <motion.div key={toast.id} initial={{
        opacity: 0,
        x: -24,
        y: 8
      }} animate={{
        opacity: 1,
        x: 0,
        y: 0
      }} exit={{
        opacity: 0,
        x: -24
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} role="status" aria-live="polite" className="pointer-events-auto flex w-[300px] max-w-[calc(100vw-2rem)] items-center gap-3 rounded-2xl border border-white/10 bg-ink-900/95 p-3 shadow-card backdrop-blur-md" data-id="element-235">
            {/* Thumbnail or icon */}
            <div className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-ink-800 ring-1 ring-white/10" data-id="element-236">
              {toast.product.thumbnailUrl ? <img src={toast.product.thumbnailUrl} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" data-id="element-237" /> : <ShoppingBagIcon className="h-5 w-5 text-accent" aria-hidden="true" data-id="element-238" />}
              <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-ink-900" data-id="element-239">
                <CheckIcon className="h-3 w-3 text-white" aria-hidden="true" data-id="element-240" />
              </span>
            </div>

            {/* Copy */}
            <div className="min-w-0 flex-1" data-id="element-241">
              <p className="truncate text-sm font-semibold text-white" data-id="element-242">
                {toast.buyer} <span className="text-silver/60" data-id="element-243">·</span>{' '}
                <span className="font-normal text-silver/70" data-id="element-244">
                  {toast.place}
                </span>
              </p>
              <p className="truncate text-xs text-silver/75" data-id="element-245">
                Compró{' '}
                <span className="font-semibold text-accent-light" data-id="element-246">
                  {toast.product.title}
                </span>
                {toast.product.editionLabel ? <span className="text-silver/55" data-id="element-247">
                    {' '}
                    ({toast.product.editionLabel})
                  </span> : null}{' '}
                <span className="font-semibold text-white" data-id="element-248">
                  {toast.product.priceText}
                </span>
              </p>
              <p className="mt-0.5 text-[10px] uppercase tracking-wide text-silver/45" data-id="element-249">
                {toast.timeAgo}
              </p>
            </div>

            <button type="button" onClick={close} aria-label="Cerrar notificación" className="shrink-0 rounded-md p-1 text-silver/50 transition-colors hover:text-white" data-id="element-250">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-251" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}